import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaCode, FaStar, FaChartLine } from 'react-icons/fa';
import { PROJECTS_DATA } from '../data/projects';
import PageTransition from '../components/PageTransition';
import { fadeInUp, staggerContainer, viewport } from '../utils/animations';

/* ─── Category colour map ───────────────────────────────────── */
const CATEGORY_COLORS = {
  'Industry 5.0': 'from-violet-600 to-purple-800',
  CleanTech: 'from-emerald-500 to-teal-700',
  HealthTech: 'from-sky-500 to-blue-700',
  FinTech: 'from-amber-500 to-orange-700',
  EdTech: 'from-pink-500 to-rose-700',
  Logistics: 'from-cyan-500 to-indigo-700',
};

const getCategoryGradient = (cat) => CATEGORY_COLORS[cat] || 'from-gray-600 to-gray-800';

/* ─── Project Card ─────────────────────────────────────────── */
const ProjectCard = ({ project, index }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 40, scale: 0.96 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, scale: 0.92, transition: { duration: 0.25 } }}
    transition={{ delay: index * 0.08, duration: 0.55, ease: [0.25, 0.1, 0.25, 1] }}
    whileHover={{ y: -8, transition: { duration: 0.2 } }}
    className="group glass-card rounded-2xl overflow-hidden card-hover-lift"
  >
    {/* Image */}
    <div className="relative h-52 overflow-hidden bg-gray-900">
      <img
        src={project.image}
        alt={project.title}
        className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/40 to-transparent" />
      <div className={`absolute inset-0 bg-gradient-to-br ${getCategoryGradient(project.category)} opacity-20 group-hover:opacity-30 transition-opacity duration-300`} />

      <div className="absolute top-4 left-4 flex flex-col gap-2">
        <span className="px-3 py-1 text-[10px] font-bold uppercase tracking-widest bg-black/50 backdrop-blur-sm border border-white/10 text-gray-300 rounded-full">
          {project.category}
        </span>
        {project.featured && (
          <span className="flex items-center gap-1 px-3 py-1 text-[10px] font-bold uppercase tracking-widest bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-full">
            <FaStar className="text-[8px] text-yellow-400" /> Featured
          </span>
        )}
      </div>

      {project.metrics?.[0] && (
        <div className="absolute bottom-4 right-4 flex items-center gap-1.5 px-3 py-1.5 bg-black/60 backdrop-blur-sm border border-white/10 rounded-full">
          <FaChartLine className="text-emerald-400 text-[10px]" />
          <span className="text-white text-xs font-bold">{project.metrics[0].value}</span>
          <span className="text-gray-400 text-[10px]">{project.metrics[0].label}</span>
        </div>
      )}
    </div>

    {/* Content */}
    <div className="p-6">
      <h3 className="text-white font-bold text-lg mb-1.5 group-hover:text-gray-100 transition-colors duration-300 leading-snug">
        {project.title}
      </h3>
      <p className="text-gray-500 text-xs font-medium italic mb-3">{project.tagline}</p>
      <p className="text-gray-400 text-sm leading-relaxed mb-5 line-clamp-2">{project.description}</p>

      <div className="flex flex-wrap gap-1.5 mb-6">
        {project.techStack.slice(0, 4).map((tech, i) => (
          <span key={i} className="px-2 py-0.5 text-[11px] bg-gray-800/80 border border-gray-700/60 text-gray-400 rounded-md">
            {tech}
          </span>
        ))}
        {project.techStack.length > 4 && (
          <span className="px-2 py-0.5 text-[11px] bg-gray-800/80 border border-gray-700/60 text-gray-500 rounded-md">
            +{project.techStack.length - 4}
          </span>
        )}
      </div>

      <Link
        to={`/projects/${project.slug}`}
        className="inline-flex items-center gap-2 text-sm font-semibold text-gray-400 group-hover:text-white transition-colors duration-300"
      >
        View Case Study
        <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform duration-300" />
      </Link>
    </div>
  </motion.div>
);

/* ─── Main Component ───────────────────────────────────────── */
const Projects = () => {
  const [activeCategory, setActiveCategory] = useState('All');

  const categories = ['All', ...new Set(PROJECTS_DATA.map((p) => p.category))];
  const filtered =
    activeCategory === 'All' ? PROJECTS_DATA : PROJECTS_DATA.filter((p) => p.category === activeCategory);

  return (
    <PageTransition>
      <div className="bg-transparent min-h-screen">

        {/* Hero */}
        <section className="relative pt-36 pb-24 overflow-hidden">
          <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none" />
          <div className="spotlight w-[600px] h-[600px] top-1/2 left-1/2" />
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="text-center max-w-4xl mx-auto">
              <motion.div variants={fadeInUp} className="inline-flex mb-6">
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-900/60 backdrop-blur-sm border border-gray-700 rounded-full badge-glow">
                  <FaCode className="text-gray-400 text-xs" />
                  <span className="text-xs font-semibold tracking-widest text-gray-300 uppercase">Our Work</span>
                </div>
              </motion.div>
              <motion.h1 variants={fadeInUp} className="text-5xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight">
                <span className="text-white">Projects That</span>
                <br />
                <span className="animated-gradient-text">Make an Impact</span>
              </motion.h1>
              <motion.p variants={fadeInUp} className="text-gray-400 text-lg md:text-xl max-w-2xl mx-auto mb-10">
                From Industry 5.0 manufacturing intelligence to blockchain-verified supply chains — platforms we've built for companies pushing the edge.
              </motion.p>
              <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-10 mt-4">
                {[
                  { value: '6+', label: 'Industries' },
                 // { value: '₹40Cr+', label: 'Client Value Unlocked' },
                  { value: '100%', label: 'On-Time Delivery' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center">
                    <div className="text-2xl font-black text-white">{stat.value}</div>
                    <div className="text-xs text-gray-500 uppercase tracking-widest mt-0.5">{stat.label}</div>
                  </div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Filter Tabs */}
        <section className="pb-10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-wrap justify-center gap-3"
            >
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`relative px-5 py-2 text-sm font-semibold rounded-full border transition-all duration-300 ${
                    activeCategory === cat
                      ? 'bg-white text-gray-950 border-white'
                      : 'bg-transparent text-gray-400 border-gray-700 hover:border-gray-500 hover:text-white'
                  }`}
                >
                  {activeCategory === cat && (
                    <motion.span
                      layoutId="active-pill"
                      className="absolute inset-0 bg-white rounded-full"
                      transition={{ type: 'spring', stiffness: 200, damping: 25 }}
                    />
                  )}
                  <span className="relative z-10">{cat}</span>
                </button>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Projects Grid */}
        <section className="py-8 pb-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <AnimatePresence mode="popLayout">
              <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {filtered.map((project, i) => (
                  <ProjectCard key={project._id} project={project} index={i} />
                ))}
              </motion.div>
            </AnimatePresence>
            {filtered.length === 0 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20 text-gray-500">
                No projects found in this category.
              </motion.div>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-24 border-t border-gray-800/60">
          <div className="spotlight w-80 h-80 top-1/2 left-1/2" />
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
            <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewport}>
              <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-black text-white mb-4">
                Want to be our next <span className="animated-gradient-text">success story?</span>
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-gray-400 text-lg mb-10 max-w-xl mx-auto">
                Share your vision with us and we'll architect something exceptional together.
              </motion.p>
              <motion.div variants={fadeInUp}>
                <Link
                  to="/contact"
                  className="group inline-flex items-center gap-2 px-10 py-4 bg-white text-gray-950 font-bold rounded-xl hover:bg-gray-100 transition-all duration-300 hover:-translate-y-1 shine-hover"
                >
                  Start a Conversation
                  <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </section>

      </div>
    </PageTransition>
  );
};

export default Projects;
